
import React, { useState } from 'react';
import { mockPages } from '../../data/mockData';
import type { WebsitePage } from '../../types';
import { SearchIcon, ContentIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';

const ITEMS_PER_PAGE = 6;

const Content: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil(mockPages.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentPages = mockPages.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const goToPage = (pageNumber: number) => {
        setCurrentPage(Math.max(1, Math.min(totalPages, pageNumber)));
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="p-4 sm:p-5">
                <div className="relative max-w-xs">
                    <label htmlFor="search-input" className="sr-only">Search</label>
                    <input
                        type="text"
                        id="search-input"
                        placeholder="Search"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#681628] focus:border-transparent"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <SearchIcon className="w-4 h-4 text-gray-400" />
                    </div>
                </div>
            </div>

            <div>
                <div className="px-5 py-3 bg-gray-50 border-t border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800">Website Pages</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-white">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pages</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Update</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Update by</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentPages.map((page: WebsitePage) => (
                                <tr key={page.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <ContentIcon className="w-5 h-5 text-gray-400 mr-3" />
                                            <div className="text-sm font-medium text-gray-900">{page.name}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{page.lastUpdate}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${page.updatedBy.bgColor}`}>
                                                {page.updatedBy.initials}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                 <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                    <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </span>
                    <div className="flex items-center gap-1">
                        <button 
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-200/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                           <ChevronLeftIcon className="w-5 h-5 text-gray-600"/>
                        </button>
                         {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                            <button
                                key={pageNumber}
                                onClick={() => goToPage(pageNumber)}
                                className={`px-3.5 py-1.5 rounded-md text-sm font-medium ${
                                    currentPage === pageNumber 
                                        ? 'bg-[#681628] text-white' 
                                        : 'bg-white text-gray-700 hover:bg-gray-200/20'
                                }`}
                            >
                                {pageNumber}
                            </button>
                        ))}
                        <button 
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-200/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                           <ChevronRightIcon className="w-5 h-5 text-gray-600"/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Content;
